import React, { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Create WhatsApp message with form data
    const message = `Hi Pro Cinemax!%0A%0A*New Enquiry*%0A%0A*Name:* ${formData.fullName}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone}%0A*Service:* ${formData.service}%0A*Message:* ${formData.message}`;
    
    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/917204247443?text=${message}`, '_blank');
    
    toast.success('Redirecting to WhatsApp...');
    setFormData({ fullName: '', email: '', phone: '', service: '', message: '' });
    setSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="text-cyan-800 text-sm font-semibold tracking-wider uppercase mb-3">
            GET IN TOUCH
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Let's Build Your Dream Cinema
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Ready to transform your home? Contact us for a free consultation.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Contact Form - Takes 3 columns */}
            <div className="lg:col-span-3 bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                    className="bg-white border-gray-300 text-gray-900 h-12"
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-white border-gray-300 text-gray-900 h-12"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="bg-white border-gray-300 text-gray-900 h-12"
                  />
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    required
                    className="flex h-12 w-full rounded-md border-2 border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-cyan-600 focus:outline-none"
                  >
                    <option value="">Select Service</option>
                    <option value="Dolby Atmos Theatre">Dolby Atmos Theatre</option>
                    <option value="4K/8K Projection">4K/8K Projection</option>
                    <option value="Acoustic Design">Acoustic Design</option>
                    <option value="Luxury Seating">Luxury Seating</option>
                    <option value="Complete Package">Complete Theatre Package</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <Textarea
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="bg-white border-gray-300 text-gray-900 resize-none"
                />

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-cyan-800 hover:bg-cyan-900 text-white font-semibold py-4 h-14 text-base rounded-lg disabled:opacity-50 shadow-lg shadow-cyan-800/30"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Info - Takes 2 columns */}
            <div className="lg:col-span-2 space-y-4">
              {/* Phone */}
              <div className="bg-gray-50 rounded-xl p-5 border-2 border-cyan-700/30 hover:border-cyan-700 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-800 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-800/30">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-0.5">Phone</h3>
                    <p className="text-cyan-800 text-sm font-semibold">+91 72042 47443</p>
                    <p className="text-gray-500 text-xs">Mon-Sat, 9AM-7PM</p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-gray-50 rounded-xl p-5 border-2 border-cyan-700/30 hover:border-cyan-700 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-800 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-800/30">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-0.5">Email</h3>
                    <p className="text-cyan-800 text-sm font-semibold">contact@procinemax.com</p>
                    <p className="text-gray-500 text-xs">24/7 Support</p>
                  </div>
                </div>
              </div>

              {/* Office */}
              <div className="bg-gray-50 rounded-xl p-5 border-2 border-cyan-700/30 hover:border-cyan-700 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-800 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-800/30">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-0.5">Office</h3>
                    <p className="text-cyan-800 text-sm font-semibold">Hennur, Bengaluru</p>
                    <p className="text-gray-500 text-xs">Karnataka 560077</p>
                  </div>
                </div>
              </div>

              {/* Full Address */}
              <div className="bg-gray-50 rounded-xl p-5 border-2 border-cyan-700/30 hover:border-cyan-700 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-800 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-800/30">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Visit Us</h3>
                    <p className="text-cyan-800 text-sm font-semibold leading-relaxed">First Floor, beside NeuBaby Baby shop @ Hennur, Geddalahalli, Rammana Layout</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
